/**
 * DigitalCheckoutModal — VARIANT 3 (digital) of the 3-variant checkout.
 *
 * 1-step flow (summary + 2FA → create → redirect).
 *
 * SPEC-081 Fase 5 §6 Task C + §4.2 (digital flow) + §0 Q3 (auto-delivered
 * on creation) + Q2 (24h fallback auto-release if no download).
 *
 * The buyer pays 100% upfront to escrow. On create, the backend sets
 * `status='delivered'` + `autoCloseAt=now+24h` immediately (Q3). The
 * download button (which triggers the actual escrow release via
 * `recordDownloadAndRelease` — INV-MKT-14) lives on the order detail
 * page (`/market/ordenes/{orderNumber}`) — Task C2.
 *
 * SPEC-244 · partición VERBATIM de
 * frontend/src/components/market/checkout-modal.tsx (2.637 L).
 *
 * El monolito original mezclaba estado + handlers + render en un único
 * componente de ~290 L. Esta vista fina se compone de:
 *   - `useDigitalCheckout` hook (toda la lógica de estado + handlers).
 *   - `DigitalSummaryStep` (Step 1 — product + escrow note + 2FA CTA).
 *   - `DigitalVerifyStep` (Step 2 — 2FA-verified banner + create CTA).
 *   - `TwoFaCheckoutDialog` (shared).
 *
 * Behavior preserved VERBATIM — only the wrapper extracts the 2 step
 * bodies into sub-components (T-PROPS-PASSING) + state into a hook
 * (T-HOOK-EXTRACTION). Note: the digital variant has NO step indicator
 * (it's a 2-step flow: summary → verify, not 3-step like physical /
 * service).
 */

'use client';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Download } from 'lucide-react';
import type { CheckoutModalProps } from '../types';
import { useDigitalCheckout } from '../hooks/use-digital-checkout';
import { DigitalSummaryStep } from './digital-summary-step';
import { DigitalVerifyStep } from './digital-verify-step';

// ─── Component ───────────────────────────────────────────────────────────────

export function DigitalCheckoutModal({
  open,
  onClose,
  product,
}: CheckoutModalProps) {
  const checkout = useDigitalCheckout({ open, onClose, product });
  const {
    step,
    setStep,
    submitting,
    submitError,
    grandTotal,
    callCreate,
    handleOpenChange,
  } = checkout;

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent
          className="sm:max-w-lg max-h-[90vh] overflow-y-auto"
          aria-describedby="checkout-digital-modal-desc"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Download className="size-5" aria-hidden="true" />
              {step === 'summary' && 'Comprar y descargar — Resumen'}
              {step === 'verify' && 'Comprar y descargar — Confirmar'}
            </DialogTitle>
            <DialogDescription id="checkout-digital-modal-desc">
              {step === 'summary' &&
                'Pago único con créditos. Descarga inmediata tras la compra.'}
              {step === 'verify' &&
                'Confirma para procesar el pago y obtener el enlace de descarga.'}
            </DialogDescription>
          </DialogHeader>

          {/* ── Single step: Summary + 2FA ── */}
          {step === 'summary' && (
            <DigitalSummaryStep
              product={product}
              grandTotal={grandTotal}
              submitting={submitting}
              submitError={submitError}
              onClose={onClose}
              onContinue={() => setStep('verify')}
            />
          )}

          {/* ── Verify (after 2FA) ── */}
          {step === 'verify' && (
            <DigitalVerifyStep
              grandTotal={grandTotal}
              submitting={submitting}
              submitError={submitError}
              onBack={() => setStep('summary')}
              onCreate={() => void callCreate()}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
